// [ai] The query client — the ONLY way the app reads the vault (docs/ARCHITECTURE.md, layer 1). query(name, params) resolves to
// the catalog's answer; every call made inside one tick travels in ONE POST /api/query (a 12-widget view = one request), split
// at the server's batch cap, and each item answers on its own: a refused item rejects its caller with the server's words while
// the siblings resolve. useAtlasQuery() is the TanStack wrapper components use; the key shape is what api/events invalidates.
import { useQuery, type UseQueryOptions } from "@tanstack/react-query";

export const MAX_BATCH = 32;   // app/lib/queries.py MAX_BATCH
export type Params = Record<string, unknown>;
type Answer = { ok: true; data: unknown } | { ok: false; error: string };
type Pending = { name: string; params: Params; resolve: (v: unknown) => void; reject: (e: unknown) => void };

export class QueryError extends Error {
  override readonly name = "QueryError";
  constructor(readonly query: string, message: string) { super(message); }
}

let pending: Pending[] = []; let scheduled = false;

export function query<T = unknown>(name: string, params: Params = {}): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    pending.push({ name, params, resolve: resolve as (v: unknown) => void, reject });
    if (!scheduled) { scheduled = true; setTimeout(flush, 0); }   // a macrotask: every hook of one render commit lands in the same batch
  });
}

function flush() {
  scheduled = false; const all = pending; pending = [];
  for (let i = 0; i < all.length; i += MAX_BATCH) void send(all.slice(i, i + MAX_BATCH));
}

async function send(items: Pending[]) {
  let out: Answer[];
  try {
    const r = await fetch("/api/query", { method: "POST", headers: { "Content-Type": "application/json", Accept: "application/json" }, body: JSON.stringify(items.map(i => ({ name: i.name, params: i.params }))) });
    if (!r.ok) throw new Error(`/api/query answered ${r.status}`);
    out = (await r.json()) as Answer[];
    if (!Array.isArray(out) || out.length !== items.length) throw new Error("/api/query answered the wrong shape");
  } catch (e) { for (const i of items) i.reject(e); return; }
  items.forEach((i, k) => { const a = out[k]; if (!a || typeof a !== "object") i.reject(new QueryError(i.name, "/api/query answered a malformed item")); else if (a.ok) i.resolve(a.data); else i.reject(new QueryError(i.name, String(a.error))); });
}

export const queryKey = (name: string, params: Params = {}) => ["q", name, params] as const;

export function useAtlasQuery<T>(name: string, params: Params = {}, opts: Omit<UseQueryOptions<T, Error>, "queryKey" | "queryFn"> = {}) {
  return useQuery<T, Error>({ queryKey: queryKey(name, params), queryFn: () => query<T>(name, params), ...opts });
}
